import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import ModalProvider from "mui-modal-provider";
import { Toaster } from "react-hot-toast";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuthStore } from "@/store";
import ErrorBoundary from "@/components/ErrorBoundary";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialized = useRef(false);
  const [shouldRender, setShouldRender] = useState(false);

  // 🔥 zustand authStore 사용
  const { isAuthenticated, isLoading, initializeAuth } = useAuthStore();

  // 인증이 필요 없는 공개 경로
  const isPublicRoute = location.pathname === "/login";

  console.log("🔍 슈퍼어드민 App.tsx 렌더링:", {
    pathname: location.pathname,
    isPublicRoute,
    isAuthenticated,
    isLoading,
    isInitialized: isInitialized.current,
    shouldRender,
  });

  // 🔥 초기 인증 체크 (앱 시작 시 한 번만)
  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized.current) {
        console.log("🚫 슈퍼어드민 이미 초기화됨 - 스킵");
        return;
      }

      console.log("🚀 슈퍼어드민 앱 초기화 시작");
      isInitialized.current = true;

      try {
        await initializeAuth();
        console.log("✅ 슈퍼어드민 앱 초기화 완료");
      } catch (error) {
        console.error("❌ 슈퍼어드민 앱 초기화 실패:", error);
      } finally {
        // 초기화 완료 후 렌더링 허용
        setShouldRender(true);
      }
    };

    initAuth();
  }, [initializeAuth]);

  // 🔥 인증 상태 변경 시 리다이렉트 처리 (초기화 완료 후에만)
  useEffect(() => {
    // 초기화가 완료되지 않았거나, 로딩 중이면 대기
    if (!isInitialized.current || !shouldRender || isLoading) {
      console.log("⏳ 슈퍼어드민 초기화 또는 로딩 중 - 리다이렉트 건너뛰기");
      return;
    }

    console.log("🔍 슈퍼어드민 인증 상태 체크:", {
      pathname: location.pathname,
      isPublicRoute,
      isAuthenticated,
      "현재 시간": new Date().toISOString(),
    });

    // 🔥 인증된 사용자가 로그인 페이지에 있는 경우 대시보드로 이동
    if (isAuthenticated && isPublicRoute) {
      console.log("✅ 인증된 사용자 - 대시보드로 이동");
      navigate("/", { replace: true });
      return;
    }

    // 🔥 인증되지 않은 사용자가 보호된 경로에 접근하려는 경우 로그인 페이지로 이동
    if (!isAuthenticated && !isPublicRoute) {
      console.warn("❌ 인증되지 않은 상태 - 로그인 페이지로 이동");
      navigate("/login", { replace: true });
      return;
    }

    console.log("✅ 슈퍼어드민 인증 상태 정상 - 현재 경로 유지");
  }, [
    location.pathname,
    isAuthenticated,
    isPublicRoute,
    isLoading,
    navigate,
    shouldRender,
  ]);

  // 🔥 초기화가 완료되지 않았으면 로딩 표시
  if (!shouldRender || isLoading) {
    return (
      <ModalProvider>
        <SnackbarProvider>
          <Toaster position="top-right" />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            gap={2}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="text.secondary">
              슈퍼어드민 시스템 로딩 중...
            </Typography>
            <Typography variant="body2" color="text.disabled">
              인증 확인 중입니다
            </Typography>
          </Box>
        </SnackbarProvider>
      </ModalProvider>
    );
  }

  // 🔥 렌더링 조건 확인
  const canRender = isAuthenticated || isPublicRoute;

  console.log("🎯 슈퍼어드민 렌더링 결정:", {
    canRender,
    isAuthenticated,
    isPublicRoute,
    pathname: location.pathname,
  });

  if (!canRender) {
    console.warn("🚫 슈퍼어드민 렌더링 조건 불만족 - 로딩 표시");
    return (
      <ModalProvider>
        <SnackbarProvider>
          <Toaster position="top-right" />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            gap={2}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="text.secondary">
              인증 상태 확인 중...
            </Typography>
          </Box>
        </SnackbarProvider>
      </ModalProvider>
    );
  }

  console.log("✅ 슈퍼어드민 정상 렌더링");

  return (
    <ErrorBoundary>
      <ModalProvider>
        <SnackbarProvider>
          <Toaster position="top-right" />
          <Outlet />
        </SnackbarProvider>
      </ModalProvider>
    </ErrorBoundary>
  );
}

export default App;
